/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  FlexibleConnectedPositionStrategy,
  Overlay,
  OverlayConfig,
  OverlayRef,
} from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  ComponentRef, Directive, DoCheck, ElementRef, EventEmitter, Host, HostBinding, HostListener, Inject, Input, NgZone, OnDestroy, OnInit, Optional, Output, Renderer2, ViewContainerRef, } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatFormField } from '@angular/material/form-field';

import { coalesce, denull, isElementFocused, stopEvent } from '@gh/core-util';
import { fromEvent$, interval$, merge$ } from '@gh/rx';
import { map$, debounce$, filter$, first$, switchMap$ } from '@gh/rx/operators';

import { compact, flatten, isNil, noop } from 'lodash';
import { Observable, Subject, Subscription } from 'rxjs';

import {
  KEY_DOWN,
  KEY_ENTER,
  KEY_ESCAPE,
  KEY_PAGE_DOWN,
  KEY_PAGE_UP,
  KEY_UP,
} from '../../../utils';
import { AutocompleteActionIconComponent } from './autocomplete-action-icon.component';
import { AutocompleteArrowIconComponent } from './autocomplete-arrow-icon.component';
import { AutocompleteMaskedTypeDirective } from './autocomplete-masked-type.directive';
import { Option } from './autocomplete-option.model';
import { AutocompleteComponent } from './autocomplete.component';
import { PLACEHOLDER_PADDING_HORIZONTAL } from './autocomplete.constants';
import { DOCUMENT } from '@angular/common';

enum AutocompleteActionType { Focus, Blur }

type AutocompleteAction = {
  type: AutocompleteActionType;
  [name: string]: any;
};

function findFormField(element: Node): Maybe<Element> {
  let parent = element.parentElement;
  while (parent && parent.tagName !== 'MAT-FORM-FIELD') {
    parent = parent.parentElement;
  }
  return denull(parent);
}

function getOverlayAnchorElement(inputElement: Node, autocomplete?: AutocompleteComponent): Element {
  if (autocomplete?.panelClass?.includes('in-cell-autocomplete-panel')) {
    return inputElement as Element;
  }
  const formFieldEl = findFormField(inputElement);
  if (!formFieldEl) {
    return inputElement as Element;
  }
  const lineRipple = formFieldEl.querySelector('.mdc-line-ripple') ||
    formFieldEl.querySelector('.mat-form-field-lineripple') ||
    formFieldEl.querySelector('[matformfieldlineripple]');
  if (lineRipple) {
    return lineRipple as Element;
  }
  return formFieldEl;
}

const PAGE_SIZE = 6;

@Directive({
  standalone: false,
  selector: '[ghAutocomplete]',
  host: {
    'class': 'autocomplete-input-element',
    'autocomplete': 'off',
  },
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: AutocompleteDirective,
    multi: true,
  }],
  exportAs: 'ghAutocompleteInput',
})
export class AutocompleteDirective implements OnInit, DoCheck, OnDestroy, ControlValueAccessor {
  @Output() clear: EventEmitter<any> = new EventEmitter();
  @Output() panelOpened = new EventEmitter();
  @Output() panelClosed = new EventEmitter();

  @Input('ghAutocomplete') autocomplete: AutocompleteComponent;
  @Input() unbound: boolean = false;
  @Input() clearable: boolean = true;

  /**
   * If ignoreFirstFocus is set, autocomplete panel will not be shown when input is focused first time.
   * This is necessary in dialogs to avoid opening of autocomplete panel when input is focused as first dialog input.
   */
  @Input()
  set ignoreFirstFocus(ignore: any) {
    this._ignoreFirstFocus = coerceBooleanProperty(ignore);
  }

  @Input()
set ignoreOutsideClickRollback(ignore: any) {
  this._ignoreOutsideClickRollback = coerceBooleanProperty(ignore);
}
private _ignoreOutsideClickRollback = false;

  @Input()
  set disablePanel(disable: any) {
    this._disablePanel = coerceBooleanProperty(disable);
  }
  private _disablePanel = false;

  clearButton: ComponentRef<AutocompleteActionIconComponent>;
  autocompleteToggleButton: ComponentRef<AutocompleteArrowIconComponent>;
  mdInputIconElement?: Element;

  private _ignoreFirstFocus: boolean;
  private action$$ = new Subject<AutocompleteAction>();

  private anchorRef: ElementRef;
  private _inputSubscription: Subscription;
  private _overlayRef: OverlayRef;
  private _portal: TemplatePortal<any>;
  private _positionStrategy: FlexibleConnectedPositionStrategy;
  private positionChangeSubscription: Subscription;
  private onTouchedCallback: () => void = noop;
  private onChange = noop;
  private _lastPanelVisible = false;
  private disabled: boolean = false;

  constructor(public _element: ElementRef,
              private _overlay: Overlay,
              private _renderer: Renderer2,
              private _viewContainerRef: ViewContainerRef,
              private _zone: NgZone,
              @Optional() @Inject(DOCUMENT) private _document: any,
              @Optional() @Host() private _inputContainer: MatFormField,
              @Optional() private maskedType?: AutocompleteMaskedTypeDirective) {
  }

  @HostBinding('class.autocomplete-panel-visible')
  get panelVisible(): boolean {
    return this.autocomplete.displayMenu && (this._overlayRef && this._overlayRef.hasAttached());
  }

  ngOnInit(): void {
    // this.autocomplete.setAutocompleteDirective(this);
    this.autocomplete.setUnbound(this.unbound);

    const anchorEl = getOverlayAnchorElement(this._element.nativeElement, this.autocomplete);
    const formFieldEl = findFormField(this._element.nativeElement);

    this.anchorRef = new ElementRef(anchorEl);

    // hack for icon positioning
    this.mdInputIconElement = formFieldEl ? denull(formFieldEl.querySelector('mat-icon')) : void 0;
    // arrow and close icon dynamic component
    this.autocompleteToggleButton = this._viewContainerRef.createComponent(AutocompleteArrowIconComponent);
    this.autocompleteToggleButton.instance.setDisabled(this.disabled);
    if (this.clearable) {
      this.clearButton = this._viewContainerRef.createComponent(AutocompleteActionIconComponent);
      this.clearButton.instance.setDisabled(this.disabled);
      this.clearButton.instance.trigger.subscribe(() => {
        this._clear(true);
      });
    }

    this.autocompleteToggleButton.instance.trigger.subscribe(($event: any) => {
      if (this.autocomplete.opened) {
        this.action$$.next({ type: AutocompleteActionType.Blur, event: $event });
      } else {
        this.action$$.next({ type: AutocompleteActionType.Focus, event: $event });
      }
    });

    const action$ = this.action$$.pipe(
      switchMap$((action) => {
        if (action.type === AutocompleteActionType.Blur) {
          return interval$(200).pipe(first$(), map$(() => action));
        } else {
          return [action];
        }
      }));

    action$.pipe(
      filter$(({ type }) => type === AutocompleteActionType.Focus))
      .subscribe(() => {
        this._element.nativeElement.focus();
        if (this._disablePanel) {
          return;
        }
        if (this._ignoreFirstFocus) {
          this._ignoreFirstFocus = false;
        } else {
          this.openPanel();
        }
      });

    this._inputSubscription = fromEvent$(this._element.nativeElement, 'input').pipe(
      filter$(({ target }) => isElementFocused(target)),
      // we use debounce (rather than debounceTime) here,
      // because debounceTime may be not initialized due to order of component initialization
      debounce$(() => interval$(this.autocomplete.debounceTime).pipe(first$())),
      map$((event: any) => event.target.value))
      .subscribe((value) => {
        if (!this._disablePanel && !this.autocomplete.opened) {
          this.openPanel();
        }
        if (!this._disablePanel) {
          this.autocomplete.typingResults(value);
        }
      });

    this.autocomplete.selectedOptionChange.pipe(
      filter$(Boolean))
      .subscribe((option: Option) => {
        this.setSelectedOption(option);
        if (!this.autocomplete.hasChips) {
          this.onChange(this.unbound ? option.text : option.value);
        }
        this.closePanel();
      });

    this.autocomplete.suggestionListHasChanged
      .subscribe(() => this._positionStrategy?.apply());
  }

  ngOnDestroy(): void {
    this.closePanel();
    this._inputSubscription.unsubscribe();
    if (this.positionChangeSubscription) {
      this.positionChangeSubscription.unsubscribe();
    }
  }

  ngDoCheck(): void {
    if (this._lastPanelVisible !== this.panelVisible) {
      this.panelVisiblityChanged();
    }
  }

  writeValue(value: any): void {
    if (isNil(value) && this.autocomplete.value) {
      this._clear(false);
    } else {
      this.autocomplete.setValue(this.maskedType ? this.maskedType.update(value) : value).pipe(
        filter$(Boolean))
        .subscribe((option: any) => {
          this.setSelectedOption(option);
        });
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedCallback = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (this._element?.nativeElement) {
      this._element.nativeElement.disabled = isDisabled;
    }
    this.autocompleteToggleButton.instance.setDisabled(isDisabled);
    if (this.clearButton) {
      this.clearButton.instance.setDisabled(isDisabled);
    }
  }

  @HostListener('focus', ['$event'])
  onInputFocus($event: any): void {
    this.action$$.next({ type: AutocompleteActionType.Focus, event: $event });
  }

  @HostListener('input', ['$event'])
  onInput($event: any): void {
    if (this.maskedType) {
      this.maskedType.update($event.target.value);
    }
    if (this.unbound) {
      this.onChange($event.target.value);
    }
  }

  @HostListener('keydown', ['$event'])
  onKeyDown($event: KeyboardEvent): void {
    const { keyCode } = $event;
    const { autocomplete } = this;
    const { displayMenu } = autocomplete;

    switch (keyCode) {
      case KEY_UP:
        stopEvent($event);
        if (displayMenu) {
          autocomplete.moveFocus(-1);
        } else {
          this.onInputFocus($event);
        }
        break;
      case KEY_DOWN:
        stopEvent($event);
        if (displayMenu) {
          autocomplete.moveFocus(1);
        } else {
          this.onInputFocus($event);
        }
        break;
      case KEY_PAGE_UP:
        stopEvent($event);
        if (displayMenu) {
          autocomplete.moveFocus(-PAGE_SIZE);
        }
        break;
      case KEY_PAGE_DOWN:
        stopEvent($event);
        if (displayMenu) {
          autocomplete.moveFocus(PAGE_SIZE);
        }
        break;
      case KEY_ENTER:
        if (displayMenu) {
          stopEvent($event);
          autocomplete.selectByIndex(autocomplete.focusedIndex);
        }
        break;
      case KEY_ESCAPE:
        if (displayMenu) {
          stopEvent($event);
          autocomplete.close();
          this.closePanel();

          if (!this.unbound) {
            this.rollbackInputText();
          }
        }
        break;
      default:
        break;
    }
  }

  openPanel(): void {
    this.createPanel();
    this.autocomplete.open();
  }

  /** Opens the autocomplete suggestion panel. */
  createPanel(): void {
    if (!this._overlayRef) {
      this._createOverlay();
    }
    this.updateOverlayDimensions();
    if (!this._overlayRef.hasAttached()) {
      this._overlayRef.attach(this._portal);
    }
    this._overlayRef.backdropClick().subscribe(() => {
      this.autocomplete.close();
    });
    if (this.autocomplete.activeWidth) {
      this._renderer.setStyle(this._element.nativeElement, 'width', this.autocomplete.controlWidth());
    }

    this.updateAnchorBackground();

    this._zone.runOutsideAngular(() => {
      requestAnimationFrame(() => {
        this._zone.run(() => {
          if (this._overlayRef && this._overlayRef.hasAttached()) {
            this.updateOverlayDimensions();
            this._positionStrategy?.apply();
          }
        });
      });
    });
  }

  /** Closes the autocomplete suggestion panel. */
  closePanel(): void {
    if (this._overlayRef && this._overlayRef.hasAttached()) {
      this._overlayRef.detach();
    }
    if (this.autocomplete.activeWidth) {
      this._renderer.setStyle(this._element.nativeElement, 'width', '100%');
    }

    this.updateAnchorBackground();
  }

  private updateOverlayDimensions(): void {
    const inputEl = this._element.nativeElement as HTMLElement;
    const anchorEl = this.anchorRef.nativeElement as HTMLElement;
    if (anchorEl === inputEl) {
      const rect = inputEl.getBoundingClientRect();
      this.autocomplete.setAnchorDimensions(rect.width, rect.height);
    } else {
      const rect = anchorEl.getBoundingClientRect();
      this.autocomplete.setAnchorDimensions(rect.width, rect.height);
    }
  }

  private _createOverlay(): void {
    this._positionStrategy = this._createOverlayPosition();
    this._portal = new TemplatePortal(this.autocomplete.template, this._viewContainerRef);
    this._overlayRef = this._overlay.create(this._getOverlayConfig());
    this._overlayRef.hostElement.classList.add('autocomplete-overlay-box');

    this.positionChangeSubscription = this._positionStrategy.positionChanges.subscribe((event) => {
      this.autocomplete.setPlaceholderPosition(<any>event.connectionPair.overlayY);
    });

    this._getOutsideClickStream().subscribe(() => {
        if (!this.unbound && !this._ignoreOutsideClickRollback) {
          this.rollbackInputText();
        }
        this.autocomplete.close();
        this.closePanel();
    });

    this._overlayRef.attachments().subscribe(() => this.panelOpened.emit());
    this._overlayRef.detachments().subscribe(() => this.panelClosed.emit());
  }

  private _getOverlayConfig(): OverlayConfig {
    const overlayConfig = new OverlayConfig({
      scrollStrategy: this._overlay.scrollStrategies.block(),
    });
    overlayConfig.positionStrategy = this._positionStrategy;
    overlayConfig.width = 1;
    overlayConfig.panelClass = compact(flatten(<string[]>[
      'autocomplete-overlay-panel',
      this.autocomplete.panelClass && this.autocomplete.panelClass.split(/\s+/),
    ]));
    return overlayConfig;
  }

  private readonly formFieldPanelOffsetY = -12;
  private readonly inCellGridPanelOffsetY = 0;

  private _createOverlayPosition(): FlexibleConnectedPositionStrategy {
    const isInCellGrid = this.autocomplete.panelClass?.includes('in-cell-autocomplete-panel');
    const strategy = this._overlay.position()
      .flexibleConnectedTo(this.anchorRef)
      .withPositions([
        { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
        { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom' },
      ])
      .withDefaultOffsetX(-PLACEHOLDER_PADDING_HORIZONTAL);
    let offsetY = this.autocomplete.panelOffsetY !== undefined && this.autocomplete.panelOffsetY !== null
      ? this.autocomplete.panelOffsetY
      : 0;
    const anchorEl = this.anchorRef?.nativeElement;
    const isInsideFormField = anchorEl && (
      anchorEl.tagName === 'MAT-FORM-FIELD' ||
      anchorEl.classList?.contains('mat-mdc-form-field') ||
      anchorEl.classList?.contains('mat-form-field') ||
      anchorEl.classList?.contains('mdc-line-ripple') ||
      anchorEl.classList?.contains('mat-form-field-lineripple') ||
      anchorEl.hasAttribute?.('matformfieldlineripple')
    );
    if (isInsideFormField && !isInCellGrid) {
      offsetY += this.formFieldPanelOffsetY;
    }
    if (isInCellGrid) {
      offsetY += this.inCellGridPanelOffsetY;
    }
    strategy.withDefaultOffsetY(offsetY);
    return strategy;
  }

  private _clear(fireEvent: boolean): void {
    if (fireEvent) {
      this.onChange(this.unbound ? '' : void 0);
    }

    this._element.nativeElement.value = '';
    this.clearButton.instance.setDisplay(false);
    this.autocompleteToggleButton.instance.setDisplay(true);

    this.autocomplete.resetTo(void 0);
  }

  private panelVisiblityChanged(): void {
    this._lastPanelVisible = this.panelVisible;
    this.updateAnchorBackground();
  }

  private updateAnchorBackground(): void {
    const formField = findFormField(this._element.nativeElement);
    const target = (formField || this.anchorRef.nativeElement) as HTMLElement;
    target.style.backgroundColor = this.panelVisible ? '#fff' : 'transparent';
  }

  private setSelectedOption(option: Option): void {
    if (this.clearButton) {
      this.clearButton.instance.setDisplay(!!option.text);
      this.autocompleteToggleButton.instance.setDisplay(!option.text);
    }
    this._element.nativeElement.value = !this.autocomplete.hasChips
      ? option.text || ''
      : '';
  }

  private rollbackInputText(): void {
    const { selectedOption } = this.autocomplete;
    this._element.nativeElement.value = coalesce(selectedOption ? selectedOption.text : void 0, '');
  }

  private _getOutsideClickStream(): Observable<any> {
    return merge$(
      fromEvent$(this._document, 'click') as Observable<MouseEvent>,
      fromEvent$(this._document, 'touchend') as Observable<TouchEvent>)
      .pipe(filter$((event) => {
        const clickTarget =
          (event.composedPath ? event.composedPath()[0] :
            event.target) as HTMLElement;
        return this._overlay && clickTarget !== this._element.nativeElement &&
          (!!this._overlayRef && !this._overlayRef.overlayElement.contains(clickTarget));
      }));
  }
}
